import React, { useEffect, useState, useMemo } from "react";
import "../../../css/QavachSpecialistTable.css";
import api from "../../../api/axiosConfig";
import TeleSpecialistModal from "../../../components/Super Admin/Core Functions/TeleSpecialistModal";
import { showToast } from '../../Shared/Toaster';


/* ============================
   NEW INTERFACE (Updated API)
============================ */

export interface PendingTeleDoctor {
  _id: string;
  auth0Id: string;
  displayName: string;
  email: string;

  metadata: {
    phone_verified: boolean;
    profile_complete: boolean;
    doctor_status: string;
    role: string;

    doctor_profile: {
      firstName: string;
      lastName: string;
      mobile: string;
      email: string;

      graduateDegree: string;
      additionalDegrees: string[];
      institutionName: string;
      practiceType: string;
      practiceInstitutionName: string;
      practiceCity: string;

      dateOfBirth: string;
      nmcRegistrationId: string;
      smcRegistrationId: string;
      fmgeOrNextRequired: boolean;

      documents: {
        name: string;
        path: string;
        mimetype: string;
      }[];
    };
  };

  roleAssignments: any[]; // change to proper type later if needed

  stats: {
    total: number;
    activeCount: number;
    disabledCount: number;
    lastAssignedAt: string | null;
    lastDisabledAt: string | null;
  };
}

/* ============================
        COMPONENT
============================ */

const TeleSpecialistTable: React.FC = () => {
  const [teleSpecialists, setTeleSpecialists] = useState<
    PendingTeleDoctor[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [selectedSpecialist, setSelectedSpecialist] =
    useState<PendingTeleDoctor | null>(null);
  const [syncing, setSyncing] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [inviting, setInviting] = useState(false);

  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ============================
        FETCH DATA
  ============================ */

  const fetchTeleSpecialists = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      const response = await api.auth_api.get<PendingTeleDoctor[]>(
        "/users/authwith-roles/full",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // setTeleSpecialists(response.data || []);
      const doctorsOnly = (response.data || []).filter(
        (user) => user.metadata?.role === "TELEDOCTOR"
      );

      setTeleSpecialists(doctorsOnly);
    } catch (error) {
      console.error("❌ Error fetching tele specialists:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      await api.auth_api.post(
        "/sync/auth0-now",
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchTeleSpecialists();
    } catch (error) {
      console.error("❌ Error syncing:", error);
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    fetchTeleSpecialists();
  }, []);

  /* ============================
        FILTERING
  ============================ */

  const filteredSpecialists = useMemo(() => {
    return teleSpecialists
      .filter((user) => {
        const fullName =
          `${user.metadata?.doctor_profile?.firstName || ""} ${user.metadata?.doctor_profile?.lastName || ""
          }`;

        return (
          fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      .filter((user) => {
        if (statusFilter === "All") return true;
        const userStatus = user.metadata?.doctor_status || "";
        return userStatus.toLowerCase() === statusFilter.toLowerCase();
      });
  }, [teleSpecialists, searchTerm, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  /* ============================
        PAGINATION
  ============================ */

  const totalPages = Math.ceil(filteredSpecialists.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const currentSpecialistsToDisplay = filteredSpecialists.slice(
    startIndex,
    endIndex
  );

  /* ============================
        INVITE
  ============================ */

  const handleAddAndInvite = async () => {
    if (!name.trim() || !email.trim()) {
      showToast("Name and Email are required", 'error');
      return;
    }

    try {
      setInviting(true);
      const token = sessionStorage.getItem("accessToken");
      if (!token) return;

      await api.auth_api.post(
        "/admin/teledoctor/invite",
        { name, email },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsModalOpen(false);
      setName("");
      setEmail("");
      await fetchTeleSpecialists();
    } catch (error) {
      console.error(error);
      showToast("Invite failed", 'error');
    } finally {
      setInviting(false);
    }
  };

  /* ============================
        RETURN UI
  ============================ */

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div className="tele-spec-title" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <h2 style={{ color: "#174350" }}>
            Tele-Specialist Directory
          </h2>

          <button
            className="sync-btn"
            onClick={handleSync}
            disabled={syncing}
          >
            {syncing ? "Syncing..." : "Sync"}
          </button>
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: "10px",
          }}
        >
          <button
            className="add-btn"
            onClick={() => setIsModalOpen(true)}
          >
            + Add Tele-Specialist
          </button>

          <div className="filters">
            <input
              type="text"
              placeholder="Search by name or email..."
              className="search-box"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
            />

            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value)
              }
            >
              <option value="All">All Statuses</option>
              <option value="submitted">Submitted</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="invited">Invited</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <p>Loading specialists...</p>
      ) : (
        <>
          <div className="table-responsive-wrapper">
            <table className="custom-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Email</th>
                <th>Name</th>
                <th>Role Type</th>
                <th>Status</th>
                <th>Role</th>
                <th>Created On</th>
                <th>Disabled On</th>
              </tr>
            </thead>

            <tbody>
              {currentSpecialistsToDisplay.length > 0 ? (
                currentSpecialistsToDisplay.map(
                  (t, index) => (
                    // <tr
                    //   key={t._id}
                    //   onClick={() =>
                    //     setSelectedSpecialist(t)
                    //   }
                    //   style={{ cursor: "pointer" }}
                    // >

                    <tr
                      key={t._id}
                      onClick={() => {
                        if (t.metadata?.doctor_status?.toLowerCase() !== "approved") {
                          setSelectedSpecialist(t);
                        }
                      }}
                      style={{
                        cursor:
                          t.metadata?.doctor_status?.toLowerCase() === "approved"
                            ? "default"
                            : "pointer",
                      }}
                    >
                      <td>
                        {startIndex + index + 1}
                      </td>

                      <td>{t.email}</td>

                      <td>
                        <b>
                          {
                            t.metadata
                              ?.doctor_profile
                              ?.firstName
                          }{" "}
                          {
                            t.metadata
                              ?.doctor_profile
                              ?.lastName
                          }
                        </b>
                      </td>

                      <td>
                        <span className="badge role tele">
                          TELE SPECIALIST
                        </span>
                      </td>

                      <td>
                        <span
                          className={`badge status ${t.metadata?.doctor_status?.toLowerCase() ||
                            "unknown"
                            }`}
                        >
                          {t.metadata
                            ?.doctor_status ||
                            "--"}
                        </span>
                      </td>

                      <td>
                        {t.metadata?.role ||
                          "TELEDOCTOR"}
                      </td>

                      <td>
                        {t.stats?.lastAssignedAt
                          ? new Date(t.stats.lastAssignedAt).toLocaleDateString()
                          : "-"}
                      </td>

                      <td>-</td>
                    </tr>
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    style={{ textAlign: "center" }}
                  >
                    No specialists match the
                    current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 0 && (
            <div className="pagination-footer">
              <p>
                Showing {startIndex + 1} to{" "}
                {Math.min(
                  endIndex,
                  filteredSpecialists.length
                )}{" "}
                of {filteredSpecialists.length}{" "}
                results
              </p>

              <div className="pagination">
                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.max(p - 1, 1)
                    )
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from(
                  { length: totalPages },
                  (_, i) => i + 1
                ).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    onClick={() =>
                      setCurrentPage(
                        pageNumber
                      )
                    }
                    className={
                      currentPage ===
                        pageNumber
                        ? "active"
                        : ""
                    }
                  >
                    {pageNumber}
                  </button>
                ))}

                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(
                        p + 1,
                        totalPages
                      )
                    )
                  }
                  disabled={
                    currentPage ===
                    totalPages
                  }
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedSpecialist && (
        <TeleSpecialistModal
          specialist={selectedSpecialist as any}
          onClose={() =>
            setSelectedSpecialist(null)
          }
        />
      )}

      {/* ADD MODAL */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Tele-Specialist</h2>
              <span
                className="close-btn"
                onClick={() =>
                  setIsModalOpen(false)
                }
              >
                ✕
              </span>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>
                  Name <span>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter full name"
                  value={name}
                  onChange={(e) =>
                    setName(e.target.value)
                  }
                />
              </div>

              <div className="form-group">
                <label>
                  Email ID <span>*</span>
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={(e) =>
                    setEmail(e.target.value)
                  }
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="invite-btn"
                onClick={handleAddAndInvite}
                disabled={inviting}
              >
                {inviting
                  ? "Inviting..."
                  : "Add & Invite"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeleSpecialistTable;